import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Companies from "@/pages/Companies";
import Deals from "@/pages/Deals";
import Origin from "@/pages/Origin";
import Risk from "@/pages/Risk";
import Legal from "@/pages/Legal";
import Projects from "@/pages/Projects";
import Valuations from "@/pages/Valuations";
import OriginPerformance from "@/pages/OriginPerformance";
import BulkDiscovery from "@/pages/BulkDiscovery";
import ConvergentWatchlist from "@/pages/ConvergentWatchlist";
import AskClara from "@/pages/AskClara";
import Settings from "@/pages/Settings";
import Landing from "@/pages/Landing";
import Register from "@/pages/Register";
import { VerifyEmail, ForgotPassword, ResetPassword } from "@/pages/AuthFlows";
import Admin from "@/pages/Admin";
import BatchImport from "@/pages/BatchImport";
import CompanyIntel from "@/pages/CompanyIntel";
import DueDiligence from "@/pages/DueDiligence";
import CommitteeWatch from "@/pages/CommitteeWatch";
import AdminLogin from "@/pages/AdminLogin";

function ProtectedShell() {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Detect OAuth callback during render (race-condition safe)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-[#C5A059] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    if (location.pathname === "/") return <Landing />;
    if (location.pathname.startsWith("/admin")) return <Navigate to="/admin/login" replace />;
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/companies" element={<Companies />} />
        <Route path="/deals" element={<Deals />} />
        <Route path="/origin" element={<Origin />} />
        <Route path="/origin/bulk" element={<BulkDiscovery />} />
        <Route path="/origin/watchlist" element={<ConvergentWatchlist />} />
        <Route path="/origin/performance" element={<OriginPerformance />} />
        <Route path="/risk" element={<Risk />} />
        <Route path="/legal" element={<Legal />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/valuations" element={<Valuations />} />
        <Route path="/ask" element={<AskClara />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/intel" element={<CompanyIntel />} />
        <Route path="/batch-import" element={<BatchImport />} />
        <Route path="/dd/:id" element={<DueDiligence />} />
        <Route path="/committee-watch" element={<CommitteeWatch />} />
        {user.role === "admin" && <Route path="/admin" element={<Admin />} />}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <Navigate to="/" replace /> : children;
}

function AppRouter() {
  const location = useLocation();
  // Synchronous detection of OAuth callback
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/*" element={<ProtectedShell />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}
